import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { formatDate } from '@/lib/dates';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { usePlayerAuth } from '@/contexts/PlayerAuthContext';
import { usePlayer } from '@/contexts/PlayerContext';
import { logger } from '@/lib/logger';
import { useScrolled } from '@/lib/useScrolled';
import { prefetchRoute } from '@/lib/routePrefetch';
import { LayoutDashboard, User, LogOut, Sun, Moon } from 'lucide-react';

const AVATAR_COLORS = [
  '#E91E8C', '#6C3CE0', '#FF6B35', '#00C2A8', '#4ECDC4',
  '#3498DB', '#9B59B6', '#E91E63', '#00BCD4', '#FF5722',
];

function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return 'P';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return parts[0][0].toUpperCase();
}

const PLAYER_NAV = [
  { to: '/player/dash', icon: LayoutDashboard, label: 'Dash', end: true },
  // Removed: the standalone "Live" tab was a duplicate of the per-pool
  // live-scoring CTAs already surfaced on every pool card in the dashboard.
  // Players wanted to launch live scoring from where the pool lives, not
  // from a separate global tab — fewer top-level destinations keeps the
  // bottom nav punchy.
];

/** True if `to` is the active route. `end: true` requires an exact match. */
function isNavActive(pathname, to, { end = false } = {}) {
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + '/');
}

/**
 * Sidebar for player pages — matches admin sidebar style (transparent,
 * inherits the page background; no `bg-card` or `border-r` so the cream/
 * grey backdrop shows through just like the admin AppShell).
 */
const PlayerSidebar = () => {
  const location = useLocation();
  return (
    <div className="flex flex-col h-full items-center pt-4" data-testid="player-sidebar">
      <div className="mb-4" data-testid="player-sidebar-brand-logo">
        <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-10 h-10 rounded-xl object-contain" />
      </div>
      <nav className="flex flex-col items-center gap-1.5" data-testid="player-sidebar-nav">
        {PLAYER_NAV.map(item => {
          const isActive = isNavActive(location.pathname, item.to, { end: item.end });
          return (
            <NavLink key={item.to} to={item.to}
              onMouseEnter={() => prefetchRoute(item.to)}
              onFocus={() => prefetchRoute(item.to)}
              className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isActive
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
              data-testid={`player-nav-${item.label.toLowerCase()}`}
              title={item.label}
              aria-label={item.label}>
              <item.icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2.2 : 1.8} />
            </NavLink>
          );
        })}
      </nav>
      <div className="flex-1" />
    </div>
  );
};

/**
 * Bottom nav for mobile — matches admin mobile nav.
 */
const PlayerMobileNav = () => {
  const location = useLocation();
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border" data-testid="player-mobile-nav">
      <div className="flex items-center justify-around py-2 px-4 max-w-lg mx-auto">
        {PLAYER_NAV.map(item => {
          const isActive = isNavActive(location.pathname, item.to, { end: item.end });
          return (
            <NavLink key={item.to} to={item.to}
              onTouchStart={() => prefetchRoute(item.to)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                isActive ? 'text-teal-600' : 'text-muted-foreground'
              }`}
              data-testid={`player-mobile-nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-5 h-5" strokeWidth={isActive ? 2.2 : 1.8} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

/**
 * Custom user-avatar dropdown — does NOT use Radix portals/popper.
 * Same rationale as TopBarUserMenu in AppShell: Radix's getBoundingClientRect
 * positioning is unreliable on iOS Safari & Android Chrome when the trigger
 * lives inside a position:sticky header (returns y=-271px instead of the
 * visible position). This menu is just a position:absolute panel anchored to
 * the trigger's wrapper.
 */
function PlayerUserMenu({ displayName, email, themeMode, setPlayerThemeMode, onProfile, onLogout }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold hover:opacity-90 transition-opacity ring-2 ring-background"
        style={{ backgroundColor: getAvatarColor(displayName) }}
        data-testid="player-user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {getInitials(displayName)}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-56 rounded-md border bg-popover text-popover-foreground shadow-lg z-50 p-1 max-h-[calc(100vh-120px)] overflow-y-auto"
          data-testid="player-user-menu-panel"
        >
          <div className="px-3 py-2">
            <p className="text-sm font-medium truncate">{displayName}</p>
            <p className="text-xs text-muted-foreground truncate">{email}</p>
          </div>
          <div role="separator" className="-mx-1 my-1 h-px bg-muted" />
          <button
            type="button"
            onClick={() => { setOpen(false); onProfile(); }}
            className="w-full text-left flex items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors hover:bg-accent active:bg-accent"
            data-testid="player-profile-link"
          >
            <User className="w-3.5 h-3.5 mr-2" /> My Profile
          </button>
          <div role="separator" className="-mx-1 my-1 h-px bg-muted" />
          <div className="px-3 py-2">
            <div className="flex items-center justify-evenly rounded-full bg-muted p-1">
              {[
                // 'system' mode was removed Feb 2026 — it caused a half-rendered
                // flash where the page bg flipped to one theme while child
                // components painted in the other. User now picks explicitly.
                { id: 'light', icon: Sun, label: 'Light' },
                { id: 'dark', icon: Moon, label: 'Dark' },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPlayerThemeMode(id)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                    themeMode === id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title={label}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.8} />
                </button>
              ))}
            </div>
          </div>
          <div role="separator" className="-mx-1 my-1 h-px bg-muted" />
          <button
            type="button"
            onClick={() => { setOpen(false); onLogout(); }}
            className="w-full text-left flex items-center gap-2 rounded-sm px-3 py-2 text-sm outline-none transition-colors hover:bg-accent active:bg-accent text-destructive"
            data-testid="player-logout"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Wraps player-facing pages with admin-style AppShell layout.
 */
export const PlayerShell = ({ children, className = '', 'data-testid': testId, ...rest }) => {
  const { playerTheme, togglePlayerTheme, setPlayerThemeMode } = usePlayer();
  const { isAuthenticated, player, logout } = usePlayerAuth();
  const { playerName, room } = usePlayer();
  const navigate = useNavigate();
  const location = useLocation();
  const themeClass = playerTheme === 'light' ? 'player-light' : 'player-dark';

  // Mirror the player theme class onto <html> so Radix portals (Dialog,
  // DropdownMenu, Tooltip, Popover etc.) which render into document.body
  // — i.e. OUTSIDE this shell div — can still read the `.player-dark`
  // CSS variables. Without this, the rules modal and 3-dot dropdowns on
  // player pages opened in light theme even when the player had picked
  // dark. We strip the classes on unmount so admin pages don't end up
  // styled with player tokens.
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.classList.add(themeClass);
    const other = themeClass === 'player-dark' ? 'player-light' : 'player-dark';
    html.classList.remove(other);
    return () => {
      html.classList.remove('player-dark', 'player-light');
    };
  }, [themeClass]);

  const displayName = player?.first_name || playerName || player?.display_name || 'Player';
  const societyName = room?.society_name || '';
  const scrolled = useScrolled();
  const scrollRef = useRef(null);

  // Reset the inner scroll container to the top on every route change.
  // Without this, iOS Safari's address-bar collapse during navigation can
  // leave the new page mounted at a non-zero scrollTop, making it look as
  // though the page loaded "scrolled down a small bit by default".
  // We run the reset in two animation frames: the first runs synchronously
  // after layout, the second runs after iOS has finished its address-bar
  // animation (which can shift the page after the initial paint).
  useEffect(() => {
    const reset = () => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
        // iOS Safari sometimes scrolls the documentElement directly when a
        // form input has been focused — window.scrollTo alone isn't enough.
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
      }
    };
    reset();
    const r1 = requestAnimationFrame(() => {
      reset();
      const r2 = requestAnimationFrame(reset);
      return () => cancelAnimationFrame(r2);
    });
    return () => cancelAnimationFrame(r1);
  }, [location.pathname]);

  // Use a minimal shell (no sidebar / no greeting) for the entire join-pool
  // flow. The URL refactor (Apr 2026) renamed /join/:code → /joinpool/:code
  // and /player/login → /login; this regex was missed at the time and was
  // dressing the gatekeeper in the dashboard chrome — confusing players who
  // think they've already joined when they haven't.
  const isGameFlow = location.pathname.startsWith('/joinpool');
  const isGatekeeper = location.pathname === '/joinpool' || location.pathname === '/player/login' || location.pathname === '/login';

  if (!isAuthenticated || isGameFlow || isGatekeeper) {
    // Light-grey backdrop for the in-pool flows (war room, picks). Keep the
    // gatekeeper / sign-in pages on the default warm cream so they match
    // the marketing-site aesthetic.
    const bgClass = isGatekeeper ? '' : 'app-bg-grey';
    return (
      <div className={`${themeClass} bg-background text-foreground min-h-screen ${bgClass} ${className}`}
        data-testid={testId || "player-shell"} {...rest}>
        {children}
      </div>
    );
  }

  // Now that 'system' is gone, `themeMode` is identical to `playerTheme` —
  // kept as a local alias for any stale references in JSX below.
  const themeMode = playerTheme === 'dark' ? 'dark' : 'light';

  return (
    <div className={`${themeClass} flex min-h-screen bg-background app-bg-grey`} data-testid={testId || "player-shell"} {...rest}>
      {/* Desktop sidebar — transparent, matches admin AppShell aesthetic */}
      <aside className="hidden md:flex flex-col w-[72px] flex-shrink-0 sticky top-0 h-screen pl-5">
        <PlayerSidebar />
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — sticky; border only appears once content has scrolled under it */}
        <div className={`sticky top-0 z-40 flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3 flex-shrink-0 transition-[background-color,backdrop-filter,border-color] duration-200 ${scrolled ? 'bg-background/85 backdrop-blur-md border-b border-border' : 'bg-transparent border-b border-transparent'}`}>
          <div className="flex items-center gap-3">
            <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-8 h-8 rounded-lg object-contain md:hidden" />
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-foreground" data-testid="player-greeting">
                Hey, {displayName}
              </h1>
              <p className="text-[10px] text-muted-foreground leading-tight">
                {societyName ? `${societyName} \u00b7 ` : ''}{formatDate(new Date().toISOString())}
              </p>
            </div>
          </div>

          <PlayerUserMenu
            displayName={displayName}
            email={player?.email}
            themeMode={themeMode}
            setPlayerThemeMode={setPlayerThemeMode}
            onProfile={() => navigate('/player/profile')}
            onLogout={() => { logout(); navigate('/', { replace: true }); }}
          />
        </div>

        {/* Page content */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto pb-20 md:pb-6">
          <div className={`px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 ${className}`}>
            {children}
          </div>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <div className="md:hidden">
        <PlayerMobileNav />
      </div>
    </div>
  );
};

/**
 * Small theme toggle for inline use.
 */
export const PlayerThemeToggle = ({ className = '' }) => {
  const { playerTheme, togglePlayerTheme } = usePlayer();
  const isDark = playerTheme === 'dark';
  const Icon = isDark ? Sun : Moon;
  return (
    <button onClick={togglePlayerTheme}
      className={`w-8 h-8 rounded-lg border border-border hover:bg-accent flex items-center justify-center transition-colors ${className}`}
      data-testid="player-theme-toggle" aria-label={isDark ? 'Switch to light' : 'Switch to dark'}>
      <Icon className="w-4 h-4 text-muted-foreground" />
    </button>
  );
};
